import { useMemo } from 'react';
import { FormattedNumber } from 'react-intl';
import classNames from 'classnames';
import Typography from '@alltrails/core/components/Typography';
import styles from './TemperatureRange.module.scss';

type TemperatureRangeProps = {
  backgroundColor?: 'primary' | 'secondary';
  currentMax: number;
  currentMin: number;
  currentTemp?: number;
  gradientStops: [number, string][]; // percentage (decimal), color
  max: number;
  min: number;
};

const TemperatureRange = ({ backgroundColor = 'primary', currentMax, currentMin, currentTemp, gradientStops, max, min }: TemperatureRangeProps) => {
  const fullRange = max - min;

  // Just a safeguard - the currentMin should never be less than the min, and the currentMax should never be greater than the max
  const clampedCurrentMin = Math.max(currentMin, min);
  const clampedCurrentMax = Math.min(currentMax, max);

  const hasCurrentTemp = currentTemp !== undefined;
  const currentDisplayTemp = hasCurrentTemp ? Math.max(clampedCurrentMin, Math.min(clampedCurrentMax, currentTemp)) : undefined;

  const linearGradientStyle = useMemo(
    () => `linear-gradient(to right${gradientStops.map(([decimal, color]) => `, ${color} ${decimal * 100}%`).join('')})`,
    [gradientStops]
  );

  return (
    <div className={styles.container}>
      <Typography color="secondary" component="span" variant={hasCurrentTemp ? 'text100bold' : 'text100'}>
        <FormattedNumber style="unit" unit="degree" unitDisplay="narrow" value={clampedCurrentMin} />
      </Typography>
      <div className={styles.rangeContainer}>
        <div className={styles.fullRange} />
        <div
          className={styles.dailyRange}
          data-testid="daily-range-gradient"
          style={{
            background: linearGradientStyle,
            left: `${Math.round(((clampedCurrentMin - min) / fullRange) * 100)}%`,
            width: `${Math.round(((clampedCurrentMax - clampedCurrentMin) / fullRange) * 100)}%`
          }}
        />
        {currentDisplayTemp !== undefined && (
          <div
            className={classNames(styles.gradientMarker, styles[backgroundColor])}
            data-testid="current-temp-marker"
            style={{ left: `${Math.round(((currentDisplayTemp - min) / fullRange) * 100)}%` }}
          />
        )}
      </div>
      <Typography component="span" variant={hasCurrentTemp ? 'text100bold' : 'text100'}>
        <FormattedNumber style="unit" unit="degree" unitDisplay="narrow" value={clampedCurrentMax} />
      </Typography>
    </div>
  );
};

export default TemperatureRange;
